import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/activities/ActivitiesList.css';

const SEARCH_STORAGE_KEY = 'activitiesList.searchState';
const SEARCH_TTL_MS = 30 * 60 * 1000; // 30 minutes

const loadSavedSearchTerm = () => {
    try {
        const raw = sessionStorage.getItem(SEARCH_STORAGE_KEY);
        if (!raw) return '';

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.term !== 'string' || typeof parsed.savedAt !== 'number') {
            sessionStorage.removeItem(SEARCH_STORAGE_KEY);
            return '';
        }

        const isExpired = Date.now() - parsed.savedAt > SEARCH_TTL_MS;
        if (isExpired) {
            sessionStorage.removeItem(SEARCH_STORAGE_KEY);
            return '';
        }

        return parsed.term;
    } catch (error) {
        sessionStorage.removeItem(SEARCH_STORAGE_KEY);
        return '';
    }
};

export default function ActivitiesList() {
    const [activities, setActivities] = useState([]);
    const [filteredActivities, setFilteredActivities] = useState([]);
    const [searchTerm, setSearchTerm] = useState(loadSavedSearchTerm);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchActivities();
    }, []);

    useEffect(() => {
        filterActivities();
    }, [searchTerm, activities]);

    useEffect(() => {
        const trimmedTerm = searchTerm.trim();
        if (trimmedTerm) {
            sessionStorage.setItem(
                SEARCH_STORAGE_KEY,
                JSON.stringify({
                    term: trimmedTerm,
                    savedAt: Date.now(),
                })
            );
        } else {
            sessionStorage.removeItem(SEARCH_STORAGE_KEY);
        }
    }, [searchTerm]);

    const getIntensityLabel = (activity) => {
        if (Array.isArray(activity.intensities) && activity.intensities.length > 0) {
            return activity.intensities
                .map((intensity) => intensity?.name)
                .filter(Boolean)
                .join(', ');
        }
        return activity.level_name || activity.intensityName || '';
    };

    const filterActivities = () => {
        if (!searchTerm.trim()) {
            setFilteredActivities(activities);
            return;
        }

        const searchLower = searchTerm.toLowerCase();
        const filtered = activities.filter(activity => {
            return (
                activity.name?.toLowerCase().includes(searchLower) ||
                activity.description?.toLowerCase().includes(searchLower) ||
                activity.programName?.toLowerCase().includes(searchLower) ||
                activity.businessName?.toLowerCase().includes(searchLower) ||
                activity.albums?.some(album => album.toLowerCase().includes(searchLower))
            );
        });
        setFilteredActivities(filtered);
    };

    const fetchActivities = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities`,
                { withCredentials: true }
            );
            
            // Group activities by ID to handle duplicates with different albums
            const activityMap = {};
            
            response.data.forEach(activity => {
                const id = activity.idactivity;
                
                if (!activityMap[id]) {
                    activityMap[id] = {
                        ...activity,
                        albums: activity.album_name ? [activity.album_name] : []
                    };
                } else if (activity.album_name && !activityMap[id].albums.includes(activity.album_name)) {
                    activityMap[id].albums.push(activity.album_name);
                }
            });
            
            // Convert the map back to an array
            const groupedActivities = Object.values(activityMap);
            
            setActivities(groupedActivities);
            setFilteredActivities(groupedActivities);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching activities:', error);
            setError('Kunne ikke laste aktiviteter');
            setLoading(false);
        }
    };

    const handleActivityClick = (activityId) => {
        navigate(`/activities/${activityId}`);
    };

    if (loading) return <div className="loading">Laster...</div>;

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Alle aktiviteter</h1>
            </div>

            <div className="search-container">
                <input
                    type="text"
                    placeholder="Søk aktiviteter..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                <i className="fas fa-search search-icon"></i>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="list-container">
                {filteredActivities.length > 0 ? (
                    filteredActivities.map(activity => (
                        <div 
                            key={activity.idactivity} 
                            className="list-item clickable"
                            onClick={() => handleActivityClick(activity.idactivity)}
                        >
                            <div className="user-info">
                                <h3>{activity.name}</h3>
                                {activity.description && (
                                    <p className="user-email">{activity.description}</p>
                                )}
                                <div className="activity-meta">
                                    {activity.environment_name && (
                                        <div className="activity-detail-row">
                                            <p className="album-label">
                                                <i className="fas fa-mountain"></i>
                                                Miljø:
                                            </p>
                                            <div className="album-tags">
                                                <span className="album-tag">{activity.environment_name}</span>
                                            </div>
                                        </div>
                                    )}
                                    {getIntensityLabel(activity) && (
                                        <div className="activity-detail-row">
                                            <p className="album-label">
                                                <i className="fas fa-bolt"></i>
                                                Intensitet:
                                            </p>
                                            <div className="album-tags">
                                                <span className="album-tag">{getIntensityLabel(activity)}</span>
                                            </div>
                                        </div>
                                    )}
                                    {(activity.minutes !== null && activity.minutes !== undefined) && (
                                        <div className="activity-detail-row">
                                            <p className="album-label">
                                                <i className="fas fa-clock"></i>
                                                Varighet:
                                            </p>
                                            <div className="album-tags">
                                                <span className="album-tag">{activity.minutes} min</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {activity.programName && (
                                    <p className="user-role">Program: {activity.programName}</p>
                                )}
                                {activity.businessName && (
                                    <p className="user-business">Bedrift: {activity.businessName}</p>
                                )}
                                {activity.albums && activity.albums.length > 0 && (
                                    <div className="activity-albums">
                                        <p className="album-label">
                                            <i className="fas fa-layer-group"></i> 
                                            {activity.albums.length === 1 ? 'Album:' : 'Album:'}
                                        </p>
                                        <div className="album-tags">
                                            {activity.albums.map((albumName, index) => (
                                                <span key={index} className="album-tag">
                                                    {albumName}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="no-items-message">
                        {searchTerm ? 'Ingen aktiviteter funnet som matcher søket ditt' : 'Ingen aktiviteter funnet'}
                    </p>
                )}
            </div>
            
            <style jsx>{`
                .activity-albums {
                    margin-top: 8px;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 8px;
                }

                .activity-meta {
                    margin-top: 8px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .activity-detail-row {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 8px;
                }
                
                .album-label {
                    font-size: 0.9rem;
                    color: #666;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    margin: 0;
                }
                
                .album-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                }
                
                .album-tag {
                    background-color: #e9f5ff;
                    color: #0066cc;
                    padding: 3px 8px;
                    border-radius: 4px;
                    font-size: 0.85rem;
                    border: 1px solid #cce4ff;
                }
            `}</style>
        </div>
    );
} 